import { useToast } from '@plaidev/sour-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Domain } from 'auth-common/domain';
import { useEffect, useRef } from 'react';
import type { IAuthRepository } from '../../../repository/Api/Auth/interface';
import { repositoryGetter } from '../../../repository/container';

import AccountGrant = Domain.Object.Account.Grant;

const repository = repositoryGetter.Auth();
const PollIntervalMs = 5_000;
const getInitialAuthInfoQueryKey = ['/auth/get-initial-auth-info'] as const;
const getBootstrapQueryKey = ['/global-navi/get-bootstrap-v2'] as const;
const queryKey = (grantKey: AccountGrant.Key.Ephemeral | undefined) => ['/auth/get-active-grant-request-status', { grantKey }] as const;

type Status = IAuthRepository.GetActiveGrantRequestStatus.Response;
type ActiveGrant = IAuthRepository.RequestActiveGrant.ActiveGrant;
type InactiveStatus = Exclude<Status, IAuthRepository.RequestActiveGrant.RequestedGrant | ActiveGrant>;

type Params = {
  grantKey: AccountGrant.Key.Ephemeral | undefined;
  onActive?: (grant: ActiveGrant) => void;
  onInactive?: (grant: InactiveStatus) => void;
};

type Result = {
  isPollingActiveGrantRequestStatus: boolean;
};

export const _pollActiveGrantRequestStatus = ({ grantKey, onActive, onInactive }: Params): Result => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const handledStatusKey = useRef<string>();
  const handledError = useRef(false);
  const previousGrantKey = useRef<AccountGrant.Key.Ephemeral | undefined>();

  if (previousGrantKey.current !== grantKey) {
    previousGrantKey.current = grantKey;
    handledStatusKey.current = undefined;
    handledError.current = false;
  }

  const { data, error, isFetching } = useQuery({
    queryKey: queryKey(grantKey),
    queryFn: async ({ signal }) => {
      if (!grantKey) return null;
      return await repository.getActiveGrantRequestStatus({ grantKey, signal });
    },
    enabled: grantKey !== undefined,
    staleTime: 0,
    refetchInterval: (query) => {
      const status = query.state.data as Status | null | undefined;
      return status?.lifecycleState === AccountGrant.LifecycleState.Requested ? PollIntervalMs : false;
    },
    throwOnError: false,
  });

  useEffect(() => {
    if (!data || data.lifecycleState === AccountGrant.LifecycleState.Requested) return;

    const statusKey = `${data.grantKey}:${data.lifecycleState}:${'id' in data ? data.id : ''}`;
    if (handledStatusKey.current === statusKey) return;
    handledStatusKey.current = statusKey;

    void Promise.all([
      queryClient.invalidateQueries({ queryKey: getInitialAuthInfoQueryKey }),
      queryClient.invalidateQueries({ queryKey: getBootstrapQueryKey }),
    ]);

    if (data.lifecycleState === AccountGrant.LifecycleState.Active) {
      onActive?.(data);
    } else {
      onInactive?.(data);
    }
  }, [data, onActive, onInactive, queryClient]);

  useEffect(() => {
    if (!error || handledError.current) return;
    handledError.current = true;
    toast.error('権限リクエストの状態を確認できませんでした');
  }, [error, toast]);

  return { isPollingActiveGrantRequestStatus: isFetching };
};
